import {
  addRateValue,
  addWithoutSingleData,
  arrayToObj,
  dateTypeMap,
  getPercentageValue,
  getRationPercentValue,
  getTimeSpaceArray,
  retainDecimals,
  sortTimeListFunc,
  uniqueArray,
} from '@op/utils';
import moment from 'moment';
import { ChartOptionsMapType } from '../MentionsSentiment/MetricsChart/utils';
import {
  brandHealthFormat,
  dealwithBrandHealth,
  getDefaultValue,
  isNilOrEmpty,
} from '@op/utils/value';
import { CommonColumnsType } from '@op/types/table';
import { LANGUAGE_EN } from '@home/_config/config';
import { LocaleType } from '@op/types/common';
import { channelColorMap, chartAllColors, NO_DATE } from '@op/constants';

type CompetitorStateListValueType = {
  account_posts: number;
  comments: number;
  game_store_reviews: number;
  mentions: number;
  video: number;
};
export type CompetitorStateListType = Array<{
  edition_unified_id: string;
  list: CompetitorStateListValueType;
  old_list: CompetitorStateListValueType;
  new_start_date: string;
  new_end_date: string;
  old_start_date: string;
  old_end_date: string;
}>;

type CompetitorStateTimeType = Array<{
  game_id?: string;
  list: any;
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  timeline: {
    time: string;
    value: number;
  }[];
}>;

export function dealwithMentionsSentimentTable(
  data: CompetitorStateListType,
  _filter: any,
) {
  const { sort } = _filter || {};
  const total: Record<string, number> = {};
  const records = (data || []).map(item => {
    const {
      edition_unified_id,
      list,
      old_list,
      new_start_date,
      new_end_date,
      old_start_date,
      old_end_date,
    } = item || {};
    const newList: any = list || {};
    const oldList: any = old_list || {};
    const newKeys = Object.keys(newList);
    const oldKeys = Object.keys(oldList);
    const keys = uniqueArray([...newKeys, ...oldKeys]);
    const valueMap = keys.reduce((pre, key) => {
      const oldKey = `${key}_old`;
      const ratioKey = `${key}_ratio`;
      const value = newList[key] || 0;
      const oldValue = oldList[key] || 0;
      const ratio = getRationPercentValue(value, oldValue, 6);
      total[`${key}_total`] = (total[`${key}_total`] || 0) + value;
      return { ...pre, [key]: value, [oldKey]: oldValue, [ratioKey]: ratio };
    }, {});

    return {
      ...valueMap,
      game: edition_unified_id,
      edition_unified_id,
      newTime: [new_start_date, new_end_date],
      oldTime: [old_start_date, old_end_date],
    };
  });

  const recordsWithTotal = records.map(val => ({
    ...val,
    ...total,
  }));

  return recordsWithTotal.sort(
    sortTimeListFunc(sort?.by || 'game', sort?.order || 'asc'),
  );
}

export function dealWithCommunityChart(
  data: any,
  _filter: any,
  keys: CommonColumnsType[],
) {
  const tableData: {
    time: string;
    name: string;
    value: number;
    ratio: number;
  }[] = [];
  const { date_type } = _filter || {};
  const { timeline, timeline_of_start_time, timeline_of_end_time, list_stats } =
    data;
  const dateType = dateTypeMap[date_type];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  const chartData: { [key: string]: string[] } = {};
  const timeList: Array<string> = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    dateType,
    format,
  );
  let isEmpty = 0;
  const valueMap = timeline?.map(val => ({
    [moment(val.time).format(format)]: {
      ...val,
    },
  }));

  const findValueForTime = (time: string, dataList: any[]) =>
    dataList?.find(item => item[time])?.[time] || {};

  timeList.forEach((time, index) => {
    const value = findValueForTime(time, valueMap);

    let oldKey = timeList?.[index - 1];
    if (index === 0) {
      oldKey = moment(time).subtract(1, dateType).format(format);
    }
    const oldValue = findValueForTime(oldKey, valueMap);
    const rowData: any = {};
    keys.forEach(({ key }: any) => {
      const keyValue = value?.[key] || 0;
      const keyOldValue = oldValue?.[key] || 0;
      const ratio = getRationPercentValue(keyValue, keyOldValue, 6);
      isEmpty += keyValue;
      if (!chartData[key]) {
        chartData[key] = [];
      }
      chartData[key].push(keyValue);
      rowData[key] = keyValue;
      rowData[`${key}_ratio`] = ratio;
    });

    tableData.push({
      time,
      ...rowData,
    });
  });

  const pieData = Object.entries(list_stats || {})
    ?.map(([key, value], index) => ({
      name: key,
      value: value ?? 0,
      itemStyle: {
        color: channelColorMap[key] ?? chartAllColors[index],
      },
    }))
    .sort((a, b) => a.value - b.value);

  tableData.sort(sortTimeListFunc('time', 'desc'));
  return {
    xAxisData: timeList,
    yAxisData: chartData,
    tableData,
    seriesData: keys,
    tableColumns: keys,
    empty: isEmpty === 0,
    pieData,
    pieTotalData: Object.values(list_stats || {})?.reduce(
      (sum, value) => sum + value,
      0,
    ),
  };
}

export const getGameColorOrName = (data: any) => {
  return (
    id: string,
    key: 'color' | 'cover' | 'entity_name' | 'all' = 'entity_name',
  ): string => {
    const item = data?.[id];

    if (key === 'all') return item;

    return item?.[key] || id;
  };
};

/**
 * 共用获取饼图数据方法
 * @param keysList ChartOptionsMapType
 * @param list 饼图相关数据
 * @returns
 */
function getPieData(keysList: ChartOptionsMapType = [], list: any) {
  let pieTotalData = 0;
  const newKeyList = keysList.filter(({ exclude = false }) => !exclude);
  const pieData = newKeyList.map(({ key, ...otherProps }) => {
    const value = list?.[key] || 0;
    pieTotalData += value;
    return {
      ...otherProps,
      itemStyle: {
        color: otherProps.color,
      },
      value,
    };
  });

  return { pieData, pieTotalData };
}

export function dealWithMentionsSentimentList(
  data: {
    list?: Array<{
      list: {
        mentions: number;
        avg_sentiment: number;
        brand_health: number;
        negative_mentions: number;
        neutral_mentions: number;
        positive_mentions: number;
      };
      old_list: {
        mentions: number;
        avg_sentiment: number;
        brand_health: number;
        negative_mentions: number;
        neutral_mentions: number;
        positive_mentions: number;
      };
      game_id: string;
    }>;
    new_start_date?: string;
    new_end_date?: string;
    old_start_date?: string;
    old_end_date?: string;
  },
  _filter: any,
  extraData: any,
) {
  const { new_start_date, new_end_date, old_start_date, old_end_date } = data;
  const { edition_unified_id } = _filter;
  const item = data.list?.find(val => val.game_id === edition_unified_id);

  const { list, old_list, game_id } = item || {};

  const newList: any = list ? dealwithBrandHealth(list) : {};
  const oldList: any = old_list ? dealwithBrandHealth(old_list) : {};

  const newKeys = Object.keys(newList);
  const oldKeys = Object.keys(oldList);
  const keys = uniqueArray([...newKeys, ...oldKeys]);
  const valueMap = keys.reduce((pre, key) => {
    const isBrandHealth = key === 'brand_health';
    const oldKey = `${key}_old`;
    const ratioKey = `${key}_ratio`;
    let value = newList[key] || 0;
    let oldValue = oldList[key] || 0;
    let ratio = getRationPercentValue(value, oldValue, 6);

    if (isBrandHealth) {
      value = isNilOrEmpty(newList[key]) ? null : brandHealthFormat(value);
      oldValue = isNilOrEmpty(oldList[key])
        ? null
        : brandHealthFormat(oldValue);
      ratio = getRationPercentValue(value, oldValue, 6);
    }

    return { ...pre, [key]: value, [oldKey]: oldValue, [ratioKey]: ratio };
  }, {});
  const rankData: { [key: string]: { game: string; value: number }[] } = {};
  keys.forEach((key: string) => {
    const sortedList = (data.list || [])?.sort((a: any, b: any) => {
      const aValue = getDefaultValue(a.list?.[key], key);
      const bValue = getDefaultValue(b.list?.[key], key);

      return bValue - aValue;
    });

    rankData[key] = (sortedList || [])?.map((val: any) => {
      const value = val.list?.[key];

      return {
        game: val.game_id,
        value: getDefaultValue(value, key),
      };
    });
  });

  return {
    ...valueMap,
    game: game_id,
    game_id,
    newTime: [new_start_date, new_end_date],
    oldTime: [old_start_date, old_end_date],
    rankData,
  };
}

/**
 * 处理Mentions & Sentiment数据
 * @param data        接口数据
 * @param filter      过滤器
 * @param keyList     渲染的列
 * @param isRatingRatio 是否为By Rating%
 * @returns
 */
export function dealWithMentionsSentimentChart(
  data: any,
  filter: any,
  keyList: ChartOptionsMapType,
  isRatingRatio = false,
) {
  const tableData: {
    time: string;
    [key: string]: string | number;
  }[] = [];
  const { date_type, by_type } = filter || {};
  const { timeline, timeline_of_start_time, timeline_of_end_time, list } =
    data || {};
  const dateType = dateTypeMap[date_type as 'daily'];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  const chartData: { [key: string]: string[] } = {};
  let isEmpty = 0;
  const timeList: Array<string> = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    dateType,
    format,
  );

  const valueMap = arrayToObj(timeline, item =>
    moment(item.time).format(format),
  );

  timeList.forEach(time => {
    const value = valueMap[time] || {};
    const rowData: any = {};
    // let oldKey = timeList?.[index - 1];
    // if (index === 0) {
    //   oldKey = moment(time).subtract(1, dateType).format(format);
    // }
    // const oldValue = findValueForTime(oldKey, valueMap);
    // 都是要的，这里一次性算出来
    const total = keyList.reduce((pre, { key, exclude }) => {
      if (exclude) return pre;
      const keyValue = value?.[key] || 0;
      return pre + keyValue;
    }, 0);
    isEmpty += total;
    (keyList || []).forEach(({ key, exclude = false }) => {
      let keyValue = getDefaultValue(value?.[key], key);
      if (key === 'steam_filtered_positive') {
        const positive = getDefaultValue(value?.positive) || 0;
        const negative = getDefaultValue(value?.negative) || 0;
        keyValue = positive > 0 ? (positive / (positive + negative)) * 100 : 0;
      }

      if (key === 'steam_review_positive') {
        const positive = getDefaultValue(value?.lifetime_positive) || 0;
        const negative = getDefaultValue(value?.lifetime_negative) || 0;
        keyValue = positive > 0 ? (positive / (positive + negative)) * 100 : 0;
      }
      // const keyOldValue = oldValue?.[key] || 0;
      // const ratio = getRationPercentValue(keyValue, keyOldValue, 6);

      if (!chartData[key]) {
        chartData[key] = [];
      }

      if (isRatingRatio && !exclude) {
        keyValue = getPercentageValue(keyValue, total, 6);
      }

      if (key === 'brand_health') {
        keyValue = dealwithBrandHealth(keyValue);
      }

      chartData[key].push(keyValue);
      rowData[key] = keyValue;
      // rowData[`${key}_ratio`] = ratio;
    });

    tableData.push({
      time,
      ...rowData,
      total,
    });
  });

  tableData.sort(sortTimeListFunc('time', 'desc'));

  return {
    xAxisData: timeList,
    yAxisData: chartData,
    tableData,
    ...getPieData(keyList, list),
    empty: isEmpty === 0,
    format,
  };
}

export function dealWithCommunityPieChart(
  data: any,
  _filter?: any,
  keyList?: any,
) {
  const tableData: {
    time: string;
    [key: string]: string | number;
  }[] = [];

  const { sort, date_type } = _filter || {};
  const { timeline, timeline_of_start_time, timeline_of_end_time, list } = data;
  const dateType = dateTypeMap[date_type];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  const chartData: { [key: string]: string[] } = {};

  const timeList: Array<string> = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    dateType,
    format,
  );

  const valueMap = timeline?.map(val => ({
    [moment(val.time).format(format)]: {
      ...val,
    },
  }));

  const findValueForTime = (time: string, dataList: any[]) =>
    dataList?.find(item => item[time])?.[time] || {};
  let timelineTotal = 0;
  timeList.forEach((time, index) => {
    const value = findValueForTime(time, valueMap);
    const rowData: any = {};
    // let oldKey = timeList?.[index - 1];
    // if (index === 0) {
    //   oldKey = moment(time).subtract(1, dateType).format(format);
    // }
    // const oldValue = findValueForTime(oldKey, valueMap);
    (keyList || []).forEach(({ key }: any) => {
      const keyValue = value?.[key] || 0;
      timelineTotal += keyValue;
      // const keyOldValue = oldValue?.[key] || 0;
      // const ratio = getRationPercentValue(keyValue, keyOldValue, 6);

      if (!chartData[key]) {
        chartData[key] = [];
      }
      chartData[key].push(keyValue);
      rowData[key] = keyValue;
      // rowData[`${key}_ratio`] = ratio;
    });

    tableData.push({
      time,
      ...rowData,
    });
  });

  tableData.sort(sortTimeListFunc('time', 'desc'));

  return {
    xAxisData: timeList,
    yAxisData: chartData,
    tableData,
    timelineTotal,
    ...getPieData(keyList, list),
  };
}

export function getEventName(
  item: { name?: string; name_cn?: string } | undefined | null,
  lang: LocaleType,
) {
  if (!item) return NO_DATE.EMPTY;
  return lang === LANGUAGE_EN ? item?.name : item?.name_cn;
}

export function getEventTypeTagClassName(type?: string) {
  const typeToClassName: { [key: string]: string } = {
    'Game Show': '--gameshow',
    Version: '--version',
    'Marketing Events': '--market',
    Announcement: '--announcement',
    Season: '--season',
    Anniversary: '--anniversary',
    Promo: '--promo',
    Holiday: '--holiday',
    'In-game Events': '--ingame',
    'Player Discussions': '--player',
    Others: '--others',
  };

  return typeToClassName[type || ''] || '--others';
}
export const dealWithSentimentSmallChart = (
  data: {
    timeline_of_end_time: string;
    timeline_of_start_time: string;
    list: Array<any>;
  },
  filter: { date_type: string },
  extraData: any,
) => {
  const { date_type } = filter || {};
  let { timeline_of_end_time, timeline_of_start_time, list } = data || {};
  let chartData: number[] = [];
  const dateType = dateTypeMap[date_type as 'daily'];
  const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  let isEmpty = 0;

  const timeList: Array<string> = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    dateType,
    format,
  );
  list = addWithoutSingleData(
    timeline_of_start_time,
    timeline_of_end_time,
    filter.date_type,
    list,
    {
      avg_sentiment: 0,
      mention: 0,
      brand_health: 0,
      negative_mention: 0,
      neutral_mention: 0,
      positive_mention: 0,
    },
  );

  const dataList = addRateValue(
    list || [],
    ['negative_mention', 'neutral_mention', 'positive_mention'],
    'mention',
    (item: any) => ({ sentiment_avg: retainDecimals(item.avg_sentiment) }),
  );

  chartData = dataList.map(val => {
    if (val.sentiment_avg) {
      isEmpty += 1;
    }
    return val.sentiment_avg;
  });

  return {
    xAxisData: timeList,
    yAxisData: chartData,
    empty: isEmpty === 0,
  };
};

export const detailPageTips = (
  records: any,
  formatMessage: any,
  detailPage: boolean,
) => {
  if (!detailPage) {
    return records;
  }

  records.push({
    text: `<span class="dbo-chart-common-formatter-tips">${formatMessage({
      id: 'Click the chart to view related reviews',
    })}</span>`,
    value: '',
    originValue: 0,
    originText: 'Click the chart to view related reviews',
    share: '',
  });

  return records;
};

export const topReviewsTips = (
  records: any,
  formatMessage: any,
  enabled: boolean,
) => {
  if (!enabled) {
    return records;
  }

  records.push({
    text: `<span class="dbo-chart-common-formatter-tips">${formatMessage({
      id: 'Click the chart to view top 3 reviews',
    })}</span>`,
    value: '',
    originValue: 0,
    originText: 'Click the chart to view top 3 reviews',
    share: '',
  });

  return records;
};

export const engagementTypeDetailPageTips = (
  records: any,
  formatMessage: any,
  detailPage: boolean,
  sortBy?: string,
) => {
  if (!detailPage || !sortBy) {
    return records;
  }

  const tipId =
    'Click the chart to view reviews. Sorted by [{sortBy}] by default.';

  records.push({
    text: `<span class="dbo-chart-common-formatter-tips">${formatMessage(
      { id: tipId },
      { sortBy: formatMessage({ id: sortBy }) },
    )}</span>`,
    value: '',
    originValue: 0,
    originText: tipId,
    share: '',
  });

  return records;
};
